import React, { useEffect, useState } from 'react'
import ModalWrapper from '../../../../../Components/ModalWrapper/ModalWrapper'
import { Role } from '../../../../../Models/AdminManagement'

interface RoleFormModalProps {
  show: boolean
  // Passing a role puts the form in edit mode
  role?: Role | null
  onClose: () => void
  onSubmit: (payload: { name: string; description?: string }) => Promise<void>
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({ show, role, onClose, onSubmit }) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const editing = !!role

  useEffect(() => {
    setName(role?.name ?? '')
    setDescription(role?.description ?? '')
    setError('')
  }, [role, show])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      setError('Please provide a role name.')
      return
    }

    try {
      setLoading(true)
      setError('')
      await onSubmit({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      })
      onClose()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to save role.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalWrapper
      showModal={show}
      closeModal={onClose}
      title={editing ? `Edit ${role?.name}` : 'Create Role'}
    >
      <form onSubmit={handleSubmit}>
        {error && <div className='alert alert-danger py-2 mb-3'>{error}</div>}

        <div className='form-group mb-3'>
          <label>Role Name *</label>
          <input
            type='text'
            className='form-control'
            placeholder='e.g. SUPPORT_ADMIN'
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <div className='text-muted mt-1' style={{ fontSize: '0.72rem' }}>
            Role names are what get assigned to administrators. Renaming one changes it everywhere
            it is already assigned.
          </div>
        </div>

        <div className='form-group mb-4'>
          <label>Description</label>
          <textarea
            className='form-control'
            rows={3}
            placeholder='What this role is for'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className='form-group mb-2'>
          <button type='submit' className='btn btn-default w-100' disabled={loading}>
            {editing ? 'Save Changes' : 'Create Role'}{' '}
            {loading && <span className='spinner-border spinner-border-sm ms-2' />}
          </button>
        </div>
      </form>
    </ModalWrapper>
  )
}

export default RoleFormModal
